import * as angular from "angular";
import * as moment from "moment";
import HttpServiceName, { HttpService } from "../http/http.service";
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import { apsEnums } from "../../shared/dto/aps.enums";
import AuthDataServiceName, { AuthDataService } from "../auth/authData.service";
import { Endpoints } from "../../shared/config/constant.module";
import { apsCommands } from "../../shared/dto/aps.commands";

let requires = [HttpServiceName, AuthDataServiceName];

export interface ReportService {
  getDeletedDefectsReport(plantId: string, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.DeletedDefectReportItem[]>;
  getDeletedGobWeightsReport(plantId: string, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.DeletedGobWeightReportItem[]>;
  getCriticalDefectsReport(plantId: string, startDate: Date, endDate?: Date):
    angular.IPromise<apsViewModels.CriticalDefectReportItem[]>;
  getCriticalDefectReportDetails(plantId: string, startDate: Date, endDate: Date, isMissingSignOff: boolean):
    angular.IPromise<apsViewModels.CriticalDefectReportDetail[]>;
  getTopDefectsReport(shopId: string, startDate: Date, endDate?: Date, maxDefects?: number, critical?: boolean, maintenance?: boolean):
    angular.IPromise<apsViewModels.TopNDefectReportItem[]>;
  getDefectReportDetails(shopId: string, defectTypeId: string, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.DropReportDetails>;
  getFurnacePullReportByShop(plantId: string, startDateTime: Date, endDateTime: Date, auditData: boolean, includeDeleted: boolean):
    angular.IPromise<apsViewModels.FurnacePullReportShopItem[]>;
  getFurnacePullReportByTank(plantId: string, startDateTime: Date, endDateTime: Date):
    angular.IPromise<apsViewModels.FurnacePullReportTankItem[]>;
  getFurnacePullReportAbandonedWeights(plantId: string, startDateTime: Date, endDateTime: Date):
    angular.IPromise<apsViewModels.FurnacePullReportAbandonedWeightItem[]>
  generateMorningMinutesReport(plantId: string, reportDate: Date, regenerate: boolean):
    angular.IPromise<any>;
  getShiftDetailReport(plantId: string, reportDate: Date, shift: number, allShifts: boolean):
    angular.IPromise<string>;
  getJobRunReport(searchCriteria: apsViewModels.JobRunSearchCriteria):
    angular.IPromise<apsViewModels.JobRunReportItem[]>;
  getJobRunReportDetails(jobRunId: string):
    angular.IPromise<apsViewModels.JobRunReportDetailsItem>;
  getJobRunReportMolds(plantId: string, shopId: string):
    angular.IPromise<string[]>;
  getToolChangeReport(shopId: string, reasonId: number, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.ToolChangeReportItemSummary[]>;
  getToolChangeReportDetails(shopId: string, reasonId: number, toolName: string, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.DropReportDetails>;
  getDowntimeReport(shopId: string, type: apsEnums.DowntimeType, location: apsEnums.DowntimeLocation, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.DowntimeSummaryReportItem[]>;
  getDowntimeReportDetails(
    shopId: string, type: apsEnums.DowntimeType, location: apsEnums.DowntimeLocation, section: string, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.DowntimeDetailsReportItem[]>;
  getDumpGateTimeReport(plantId: string, shopId: string, shiftId: string, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.DumpGateTimeReportItem[]>;
  getHourlyChecklistReport(
    plantId: string, location: apsEnums.LocationType, locationId: string,
    checkListId: string, reportDate: Date, failuresOnly: boolean, reportEndDate: Date,
    completedOnly: boolean):
    angular.IPromise<apsViewModels.HourlyCheckListReport>;
  getMorningMinutesReport(plantId: string, reportDate: Date): void;
  getQualificationsReport(
    shopId: string, startDate: Date, endDate: Date, location: apsEnums.Location, exceptionTime: number, exceptionsOnly: boolean):
    angular.IPromise<apsViewModels.QualificationReportItem[]>;
  getDefectsReport(shopId: string, defectTypeId: string, startDate: Date, endDate: Date, severity: apsEnums.Severity):
    angular.IPromise<apsViewModels.DefectsReportItem[]>;
  getDefectsReportAll(defectTypeId: string, startDate: Date, endDate: Date, severity: apsEnums.Severity):
    angular.IPromise<apsViewModels.DefectsReportItem[]>;
  getProductionReport(plantId: string, shopId: string, userId: string, scheduleOn: Date, checklistInstanceId: string): angular.IPromise<apsViewModels.ProductionReport>;
  getProductionShiftReport(plantId: string, reportDate: Date):
    angular.IPromise<apsViewModels.ProductionShiftReportData>;
  getProductionShiftReportV2(plantId: string, reportDate: Date):
    angular.IPromise<apsViewModels.ProductionShiftReportData>;
  getDowntimeShiftReport(request: apsViewModels.DowntimeShopSectionsV1Request):
    angular.IPromise<apsViewModels.DowntimeShopSectionsV1>;
  getDowntimeInsight(request: apsViewModels.DowntimeInsightRequest):
    angular.IPromise<apsViewModels.DowntimeInsightResponse>;
  updatePlantKpi(command: apsCommands.UpdatePlantKpi): angular.IPromise<any>;
  updateHotEndBonus(command: apsCommands.UpdateHotEndBonus): angular.IPromise<any>;
  getPlantKpis(shopId: string, reportDate: Date): angular.IPromise<apsViewModels.PlantKpiItem[]>;
  getPalletKpis(shopId: string, reportDate: Date):
    angular.IPromise<apsViewModels.PalletKpiItem[]>;
  updatePalletKpi(palletUpdate: apsCommands.ChangeKpiPallets): angular.IPromise<any>;
  getWareHeldReport(plantId: string, shopId: string, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.WareHeldReportItem[]>;
  getTankMetricsReport(plantId: string, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.TankMetricsReportItem[]>;
  getWeeklyKpisReport(plantId: string):
    angular.IPromise<apsViewModels.PlantWeeklyKPIReport>;
  exportToExcel(reportType: apsEnums.ExcelExports, plantId: string, reportDate: Date): angular.IPromise<void>;
  exportToExcelV2(plantName: string, productionDate: Date, b64Data: any);
  exportToPDFV2(plantName: string, productionDate: Date, b64Data: any);
  getPlantHotEndBonus(plantId: string, reportDate: Date): angular.IPromise<apsViewModels.HotEndBonusDetails>;
  updateSignOffUsers(signOffusers : apsViewModels.SignOffItems): angular.IPromise<apsViewModels.SignOffItems>;
  getHotEndSignOff(plantId: string, productionDate:Date) : angular.IPromise<any>;
}

class ReportSvcImpl implements ReportService {
  private httpService: HttpService;
  private $window: angular.IWindowService;
  private authDataService: AuthDataService;
  private $q: angular.IQService;

  constructor(
    httpService: HttpService,
    $window: angular.IWindowService,
    authDataService: AuthDataService,
    $q: angular.IQService) {

    this.httpService = httpService;
    this.$window = $window;
    this.authDataService = authDataService;
    this.$q = $q;
  }

  exportToExcel(reportType: apsEnums.ExcelExports, plantId: string, reportDate: Date): angular.IPromise<void> {
    let defer = this.$q.defer<void>();

    var encode = (component: string) => {
      return (<any>(this.$window)).encodeURIComponent(component);
    };
    let params = {
      plantId: plantId,
      reportDate: reportDate
    };
    let url: string = null;
    let token = this.authDataService.getToken();

    switch (reportType) {
      case apsEnums.ExcelExports.ProductionReport:
        url = Endpoints.PRODUCTION_SHIFT_REPORT_EXPORT;
        params = {
          plantId: plantId,
          reportDate: reportDate
        };
        break;
    }

    if (url) {
      let fileUrl = this.httpService.getUrl(url) +
        '?plantId=' + encode(plantId) +
        '&reportDate=' + moment(reportDate).toISOString() +
        '&accessToken=' + token;

      this.$window.location.href = fileUrl;
      defer.resolve();
    } else {
      defer.reject("Report cannot be exported.");
    }

    return defer.promise;
  }

  exportToExcelV2(plantName: string, productionDate: Date, b64Data: any) {
    const byteCharacters = atob (b64Data);
    const byteNumbers = new Array (byteCharacters.length);
    for (let i = 0; i <byteCharacters.length; i ++) {
      byteNumbers [i] = byteCharacters.charCodeAt (i);
    }
    const byteArray = new Uint8Array (byteNumbers);
    let blob = new Blob ([byteArray], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
    const url = window.URL.createObjectURL (blob);
    const anchor = document.createElement ('a');
    var reportDate = moment(productionDate).format("MM/DD/YYYY");
    anchor.download = 'Production_Report_'+ plantName + '_'+reportDate+'.xlsx';
    anchor.href = url;
    anchor.click ();
  }

  exportToPDFV2(plantName: string, productionDate: Date, b64Data: any) {
    const byteCharacters = atob (b64Data);
    const byteNumbers = new Array (byteCharacters.length);
    for (let i = 0; i <byteCharacters.length; i ++) {
      byteNumbers [i] = byteCharacters.charCodeAt (i);
    }
    const byteArray = new Uint8Array (byteNumbers);
    let blob = new Blob ([byteArray], {type: 'application/pdf'});
    const url = window.URL.createObjectURL (blob);
    const anchor = document.createElement ('a');
    var reportDate = moment(productionDate).format("MM/DD/YYYY");
    anchor.download = 'Production_Report_'+ plantName + '_'+reportDate+'.pdf';
    anchor.href = url;
    anchor.click ();
  }

  getDeletedDefectsReport(plantId: string, startDate: Date, endDate: Date) {
    let params = {
      plantId: plantId,
      startDate: startDate,
      endDate: endDate
    };

    return this.httpService.get<apsViewModels.DeletedDefectReportItem[]>(Endpoints.DELETED_DEFECTS_REPORT, params);
  }

  getCriticalDefectReportDetails(plantId: string, startDate: Date, endDate: Date, isMissingSignOff: boolean) {
    let params = {
      plantId: plantId,
      startDate: startDate,
      endDate: endDate,
      isMissingSignOff: isMissingSignOff
    };

    return this.httpService.get<apsViewModels.CriticalDefectReportDetail[]>(Endpoints.CRITICAL_DEFECT_REPORT_DETAILS, params);
  }

  getDeletedGobWeightsReport(plantId: string, startDate: Date, endDate: Date) {
    let params = {
      plantId: plantId,
      startDate: startDate,
      endDate: endDate
    };

    return this.httpService.get<apsViewModels.DeletedGobWeightReportItem[]>(Endpoints.DELETED_GOB_WEIGHT_REPORT, params);
  }
  getCriticalDefectsReport(
    plantId: string,
    startDate: Date,
    endDate?: Date): angular.IPromise<apsViewModels.CriticalDefectReportItem[]> {
    var params = {
      plantId: plantId,
      startDate: startDate,
      endDate: endDate
    };

    return this.httpService.get<apsViewModels.CriticalDefectReportItem[]>(Endpoints.CRITICAL_DEFECTS_REPORT, params);
  }

  getTopDefectsReport(shopId: string, startDate: Date, endDate?: Date, maxDefects?: number, critical?: boolean, maintenance?: boolean):
    angular.IPromise<apsViewModels.TopNDefectReportItem[]> {
    var params = {
      shopId: shopId,
      startDate: startDate,
      endDate: endDate,
      maxDefects: maxDefects,
      critical: critical,
      maintenance: maintenance
    };

    return this.httpService.get<apsViewModels.TopNDefectReportItem[]>(Endpoints.TOP_N_DEFECTS_REPORT, params);
  }

  getDefectReportDetails(shopId: string, defectTypeId: string, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.DropReportDetails> {
    var params = {
      shopId: shopId,
      defectTypeId: defectTypeId,
      startDate: startDate,
      endDate: endDate
    };

    return this.httpService.get<apsViewModels.DropReportDetails>(Endpoints.DEFECT_REPORT_DETAILS, params);
  }

  getFurnacePullReportByShop(plantId: string, startDateTime: Date, endDateTime: Date, auditData: boolean, includeDeleted: boolean):
    angular.IPromise<apsViewModels.FurnacePullReportShopItem[]> {
    var params = {
      plantId: plantId,
      startTimeStamp: moment(startDateTime).toISOString(true),
      endTimeStamp: moment(endDateTime).toISOString(true),
      auditData: auditData,
      includeDeleted: includeDeleted
    };

    return this.httpService.get<apsViewModels.FurnacePullReportShopItem[]>(Endpoints.FURNACE_PULL_REPORT_SHOP, params);
  }

  getFurnacePullReportByTank(plantId: string, startDateTime: Date, endDateTime: Date):
    angular.IPromise<apsViewModels.FurnacePullReportTankItem[]> {
    var params = {
      plantId: plantId,
      startTimeStamp: moment(startDateTime).toISOString(true),
      endTimeStamp: moment(endDateTime).toISOString(true)
    };

    return this.httpService.get<apsViewModels.FurnacePullReportTankItem[]>(Endpoints.FURNACE_PULL_REPORT_TANK, params);
  }

  getFurnacePullReportAbandonedWeights(plantId: string, startDateTime: Date, endDateTime: Date):
    angular.IPromise<apsViewModels.FurnacePullReportAbandonedWeightItem[]> {
    var params = {
      plantId: plantId,
      startTimeStamp: moment(startDateTime).toISOString(true),
      endTimeStamp: moment(endDateTime).toISOString(true)
    };

    return this.httpService.get<apsViewModels.FurnacePullReportAbandonedWeightItem[]>(Endpoints.FURNACE_PULL_REPORT_ABANDONED_WEIGHTS, params);
  }

  generateMorningMinutesReport(plantId: string, reportDate: Date, regenerate: boolean): angular.IPromise<any> {
    var params = {
      plantId: plantId,
      date: reportDate,
      regenerate: regenerate
    };
    return this.httpService.post<any>(Endpoints.GENERATE_MORNING_MINUTES_REPORT, params);
  }

  getMorningMinutesReport(plantId: string, reportDate: Date): void {
    var encode = (component: string) => {
      return (<any>(this.$window)).encodeURIComponent(component);
    };

    var token = this.authDataService.getToken();

    var url = this.httpService.getUrl(Endpoints.MORNING_MINUTES_REPORT) +
      '?plantId=' + encode(plantId) +
      '&reportDate=' + moment(reportDate).toISOString() +
      '&accessToken=' + token;

    this.$window.location.href = url;
  }

  getShiftDetailReport(plantId: string, reportDate: Date, shift: number, allShifts: boolean) {
    var params = {
      plantId: plantId,
      reportDate: reportDate,
      shift: shift,
      allShifts: allShifts
    };
    return this.httpService.post<string>(Endpoints.SHIFT_DETAIL_REPORT, params);
  }

  // Guid plantId, Guid shopId, Guid userId, string scheduleOn, Guid checklistInstanceId
  getProductionReport(
    plantId: string,
    shopId: string,
    userId: string,
    scheduleOn: Date,
    checklistInstanceId: string): angular.IPromise<apsViewModels.ProductionReport> {

    var params = {
      plantId: plantId,
      shopId: shopId,
      userId: userId,
      scheduleOn: scheduleOn,
      checklistInstanceId: checklistInstanceId
    };

    return this.httpService.get<apsViewModels.ProductionReport>(Endpoints.PRODUCTION_REPORT, params);
  }
  getJobRunReport(searchCriteria: apsViewModels.JobRunSearchCriteria): angular.IPromise<apsViewModels.JobRunReportItem[]> {
    return this.httpService.post<apsViewModels.JobRunReportItem[]>(Endpoints.JOB_REPORT, searchCriteria);
  }

  getJobRunReportDetails(jobRunId: string): angular.IPromise<apsViewModels.JobRunReportDetailsItem> {
    return this.httpService.get<apsViewModels.JobRunReportDetailsItem>(Endpoints.JOB_REPORT_DETAILS, { jobRunId: jobRunId });
  }

  getJobRunReportMolds(plantId: string, shopId: string): angular.IPromise<string[]> {
    var params = {
      plantId: plantId,
      shopId: shopId
    };

    return this.httpService.get<string[]>(Endpoints.JOB_REPORT_MOLDS, params);
  }

  getToolChangeReport(
    shopId: string,
    reasonId: number,
    startDate: Date,
    endDate: Date): angular.IPromise<apsViewModels.ToolChangeReportItemSummary[]> {

    var params = {
      shopId: shopId,
      reason: reasonId,
      startDate: startDate,
      endDate: endDate
    };

    return this.httpService.get<apsViewModels.ToolChangeReportItemSummary[]>(Endpoints.TOOL_CHANGE_REPORT, params);
  }

  getToolChangeReportDetails(
    shopId: string,
    reasonId: number,
    toolName: string,
    startDate: Date,
    endDate: Date): angular.IPromise<apsViewModels.DropReportDetails> {

    var params = {
      shopId: shopId,
      reason: reasonId,
      toolName: toolName,
      startDate: startDate,
      endDate: endDate
    };

    return this.httpService.get<apsViewModels.DropReportDetails>(Endpoints.TOOL_CHANGE_REPORT_DETAILS, params);
  }

  getDowntimeReport(shopId: string, type: apsEnums.DowntimeType, location: apsEnums.DowntimeLocation, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.DowntimeSummaryReportItem[]> {
    var params = {
      shopId: shopId,
      type: type,
      location: location,
      startDate: startDate,
      endDate: endDate
    };

    return this.httpService.get<apsViewModels.DowntimeSummaryReportItem[]>(Endpoints.DOWNTIME_REPORT, params);
  }

  getDowntimeReportDetails(
    shopId: string, type: apsEnums.DowntimeType, location: apsEnums.DowntimeLocation, section: string, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.DowntimeDetailsReportItem[]> {
    var params = {
      shopId: shopId,
      type: type,
      location: location,
      section: section,
      startDate: startDate,
      endDate: endDate
    };

    return this.httpService.get<apsViewModels.DowntimeDetailsReportItem[]>(Endpoints.DOWNTIME_REPORT_DETAILS, params);
  }

  getDumpGateTimeReport(plantId: string, shopId: string, shiftId: string, startDate: Date, endDate: Date):
    angular.IPromise<apsViewModels.DumpGateTimeReportItem[]> {
    var params = {
      plantId: plantId,
      shopId: shopId,
      shiftId: shiftId,
      startDate: startDate,
      endDate: endDate
    };

    return this.httpService.get<apsViewModels.DumpGateTimeReportItem[]>(Endpoints.DUMPGATETIME_REPORT, params);
  }
  getHourlyChecklistReport(
    plantId: string,
    location: apsEnums.LocationType,
    locationId: string,
    checkListId: string,
    reportDate: Date,
    failuresOnly: boolean,
    reportEndDate: Date,
    completedOnly: boolean): angular.IPromise<apsViewModels.HourlyCheckListReport> {

    return this.httpService.get<apsViewModels.HourlyCheckListReport>(Endpoints.HOURLY_CHECKLIST_REPORT, {
      plantId: plantId,
      locationType: location,
      locationId: locationId,
      checkListId: checkListId,
      reportDate: reportDate,
      failuresOnly: failuresOnly,
      reportEndDate: reportEndDate,
      completedOnly: completedOnly
    });
  }

  getQualificationsReport(
    shopId: string, startDate: Date, endDate: Date, location: apsEnums.Location, exceptionTime: number, exceptionsOnly: boolean):
    angular.IPromise<apsViewModels.QualificationReportItem[]> {
    return this.httpService.get<apsViewModels.QualificationReportItem[]>(Endpoints.QUALIFICATIONS_REPORT, {
      shopId: shopId,
      startDate: startDate,
      endDate: endDate,
      location: location,
      exceptionTime: exceptionTime,
      exceptionsOnly: exceptionsOnly
    });
  }

  getDefectsReport(shopId: string, defectTypeId: string, startDate: Date, endDate: Date, severity: apsEnums.Severity):
    angular.IPromise<apsViewModels.DefectsReportItem[]> {

    return this.httpService.get<apsViewModels.DefectsReportItem[]>(Endpoints.DEFECTS_REPORT, {
      shopId: shopId,
      defectTypeId: defectTypeId,
      startDate: startDate,
      endDate: endDate,
      severity: severity
    });
  }

  getDefectsReportAll(defectTypeId: string, startDate: Date, endDate: Date, severity: apsEnums.Severity):
    angular.IPromise<apsViewModels.DefectsReportItem[]> {
    return this.httpService.get<apsViewModels.DefectsReportItem[]>(Endpoints.DEFECTS_REPORT_ALL, {
      defectTypeId: defectTypeId,
      startDate: startDate,
      endDate: endDate,
      severity: severity
    });
  }

  getProductionShiftReport(plantId: string, reportDate: Date):
    angular.IPromise<apsViewModels.ProductionShiftReportData> {
    // TODO: hookup params to API call when ready
    return this.httpService.get<apsViewModels.ProductionShiftReportData>(Endpoints.PRODUCTION_SHIFT_REPORT, {
      plantId: plantId,
      reportDate: reportDate
    });
  }

  getProductionShiftReportV2(plantId: string, reportDate: Date):
    angular.IPromise<apsViewModels.ProductionShiftReportData> {
    // TODO: hookup params to API call when ready
    return this.httpService.get<apsViewModels.ProductionShiftReportData>(Endpoints.PRODUCTION_SHIFT_REPORTV2, {
      plantId: plantId,
      reportDate: reportDate
    });
  }

  getDowntimeShiftReport(request: apsViewModels.DowntimeShopSectionsV1Request):
    angular.IPromise<apsViewModels.DowntimeShopSectionsV1> {
    return this.httpService.post<apsViewModels.DowntimeShopSectionsV1>(Endpoints.DOWNTIME_SHIFT_REPORTV1, request);
  }

  getDowntimeInsight(request: apsViewModels.DowntimeInsightRequest):
    angular.IPromise<apsViewModels.DowntimeInsightResponse> {
    return this.httpService.post<apsViewModels.DowntimeInsightResponse>(Endpoints.DOWNTIME_SHIFT_REPORT_INSIGHT_ALL, request);
  }

  getPalletKpis(shopId: string, reportDate: Date):
    angular.IPromise<apsViewModels.PalletKpiItem[]> {
    return this.httpService.get<apsViewModels.PalletKpiItem[]>(Endpoints.PALLET_KPIS, {
      shopId: shopId,
      reportDate: reportDate
    });
  }
  updatePalletKpi(palletUpdate: apsCommands.ChangeKpiPallets) {
    return this.httpService.post(Endpoints.UPDATE_PALLETS, palletUpdate);
  }
  getPlantKpis(shopId: string, reportDate: Date) {
    return this.httpService.get<apsViewModels.PlantKpiItem[]>(Endpoints.PLANT_KPI, {
      shopId: shopId,
      reportDate: reportDate
    });
  }
  updatePlantKpi(command: apsCommands.UpdatePlantKpi) {
    return this.httpService.put(Endpoints.PLANT_KPI, command);
  }

  updateHotEndBonus(command: apsCommands.UpdateHotEndBonus) {
    return this.httpService.put(Endpoints.PLANT_HOTENDBONUS, command);
  }


  getHotEndSignOff(plantId: string, productionDate: Date){
    let params = {
      plantId: plantId,
      productionDate: productionDate
    };
    return this.httpService.get<any>(Endpoints.GET_HOTENDBONUSSIGNOFF, params);
  }

  updateSignOffUsers(hotEndCommand : apsViewModels.SignOffItems) {
    return this.httpService.put<apsViewModels.SignOffItems>(Endpoints.SIGNOFF_HOTENDBONUS, hotEndCommand);
  }

  getWareHeldReport(plantId: string, shopId: string, startDate: Date, endDate: Date) {
    let params = {
      plantId: plantId,
      shopId: shopId,
      startDate: startDate,
      endDate: endDate
    };

    return this.httpService.get<apsViewModels.WareHeldReportItem[]>(Endpoints.WARE_HELD_REPORT, params);
  }

  getTankMetricsReport(plantId: string, startDate: Date, endDate: Date) {
    let params = {
      plantId: plantId,
      startDate: startDate,
      endDate: endDate
    };

    return this.httpService.get<apsViewModels.TankMetricsReportItem[]>(Endpoints.TANK_METRICS_REPORT, params);
  }

  getWeeklyKpisReport(plantId: string):
    angular.IPromise<apsViewModels.PlantWeeklyKPIReport> {
    return this.httpService.get<apsViewModels.PlantWeeklyKPIReport>(Endpoints.WEEKLY_KPI_REPORT, {
      plantId: plantId
    });
  }

  getPlantHotEndBonus(plantId: string, reportDate: Date) {
    return this.httpService.get<apsViewModels.HotEndBonusDetails>(Endpoints.PLANT_HOTENDBONUS, {
      plantId: plantId,
      reportDate: reportDate
    });
  }
}

export default angular
  .module('aps.services.reports', requires)
  .service('reportService', ReportSvcImpl)
  .name;


// WEBPACK FOOTER //
// ./src/app/services/reports/report.service.ts